import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ImageGallery } from '@components/image-gallery';
import { useToast } from '@hooks/use-toast/use-toast';

import { ImageGalleryFieldProps } from './image-gallery-field.type';

const GALLERY_MAX_IMAGES = 10;
const GALLERY_MAX_FILE_SIZE_MB = 12;
const GALLERY_CROP_ASPECT_RATIO = 1;

/**
 * Domain-agnostic adapter between any `GalleryImagesController` (returned
 * by `useGalleryImages` or a domain-specific wrapper such as
 * `useAddonImages`) and the reusable `<ImageGallery />` primitive. Owns
 * nothing — it forwards the controller's mutations to the gallery and
 * pulls translated copy from a configurable i18n namespace.
 *
 * The `namespace` prop defaults to `'addons'` so existing callers
 * (B2B-1522 addon, B2B-1613 addon-preset) keep working untouched.
 * Admission callers (B2B-1572) pass `'offers'` to read the same 13 keys
 * duplicated under the offers namespace. Surface-specific divergence
 * (e.g. different copy per surface) is left as a separate concern — if
 * it ever appears, swap the `namespace` enum for a `labels` prop or a
 * key-builder callback.
 */
export const ImageGalleryField = ({ controller, namespace = 'addons' }: ImageGalleryFieldProps) => {
  const { t } = useTranslation();
  const toast = useToast();

  const cropConfig = useMemo(
    () => ({
      aspectRatio: GALLERY_CROP_ASPECT_RATIO,
      translations: {
        save: t(`${namespace}:imageGalleryCropSave`),
        cancel: t(`${namespace}:imageGalleryCropCancel`),
        zoom: t(`${namespace}:imageGalleryCropZoom`),
        rotate: t(`${namespace}:imageGalleryCropRotate`),
      },
    }),
    [t, namespace]
  );

  const handleFileTooLarge = () => {
    toast.push({
      title: t(`${namespace}:imageGalleryFileTooLargeError`, { maxMB: GALLERY_MAX_FILE_SIZE_MB }),
      text: '',
      type: 'error',
    });
  };

  const handleFileTypeRejected = () => {
    toast.push({ title: t(`${namespace}:imageGalleryFileTypeRejectedError`), text: '', type: 'error' });
  };

  return (
    <div>
      <ImageGallery
        images={controller.images}
        maxImages={GALLERY_MAX_IMAGES}
        maxFileSizeMB={GALLERY_MAX_FILE_SIZE_MB}
        onUpload={controller.upload}
        onReorder={controller.reorder}
        onDelete={controller.delete}
        onFileTooLarge={handleFileTooLarge}
        onFileTypeRejected={handleFileTypeRejected}
        emptyDropzoneTitle={
          <Trans
            i18nKey={`${namespace}:imageGalleryEmptyTitle`}
            // The EN string is "Drag and drop images or <1>browse files</1>" — the styled
            // span must map to tag index 1 (an array would map it to <0> and drop the styling).
            components={{ 1: <span key='browse' className='underline font-semibold' /> }}
          />
        }
        emptyDropzoneSubtitle={t(`${namespace}:imageGalleryEmptySubtitle`, { maxMB: GALLERY_MAX_FILE_SIZE_MB })}
        uploadButtonAriaLabel={t(`${namespace}:imageGalleryUploadAria`)}
        deleteButtonAriaLabel={t(`${namespace}:imageGalleryDeleteAria`)}
        maxReachedMessage={t(`${namespace}:imageGalleryMaxReached`, { maxImages: GALLERY_MAX_IMAGES })}
        cropConfig={cropConfig}
      />
    </div>
  );
};
